import { useEffect, useState, FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import axiosInstance from '../../../lib/axios';
import { CreditAccount, SetCreditLimitPayload } from '../../../types/credit';

interface SetCreditLimitModalProps {
  open: boolean;
  onClose: () => void;
  accountId: string;
  creditLimit?: number | null;
}

const formatCurrency = (amount: number | null | undefined) => {
  if (amount === null || amount === undefined) return '—';
  return new Intl.NumberFormat('fr-DZ', {
    style: 'currency',
    currency: 'DZD',
    minimumFractionDigits: 0,
  }).format(amount);
};

// A limit below the outstanding balance is rejected by the backend with a 400 whose message names
// both figures, so it is shown verbatim rather than replaced by a generic key. class-validator
// failures arrive as an array of strings.
const extractApiMessage = (error: unknown): string | null => {
  const message = (error as { response?: { data?: { message?: string | string[] } } })?.response
    ?.data?.message;
  if (Array.isArray(message)) return message.join(', ');
  return typeof message === 'string' && message.length > 0 ? message : null;
};

export const SetCreditLimitModal = ({
  open,
  onClose,
  accountId,
  creditLimit,
}: SetCreditLimitModalProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [limit, setLimit] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setLimit(creditLimit === null || creditLimit === undefined ? '' : String(creditLimit));
      setSubmitted(false);
      setApiError(null);
    }
  }, [open, creditLimit]);

  const parsedLimit = Number(limit);
  const limitInvalid = limit.trim() === '' || !Number.isFinite(parsedLimit) || parsedLimit < 0;

  const setCreditLimitMutation = useMutation({
    mutationFn: async (payload: SetCreditLimitPayload): Promise<CreditAccount> => {
      const response = await axiosInstance.put<CreditAccount>(
        `/credit/accounts/${accountId}/limit`,
        payload,
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['credit-account', accountId] });
      queryClient.invalidateQueries({ queryKey: ['credit-accounts'] });
      onClose();
    },
    onError: (error: unknown) => {
      setApiError(extractApiMessage(error) || t('credit.setCreditLimit.error'));
    },
  });

  const isSubmitting = setCreditLimitMutation.isPending;

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    setSubmitted(true);
    setApiError(null);
    if (limitInvalid || !accountId) return;
    setCreditLimitMutation.mutate({ creditLimit: parsedLimit });
  };

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{t('credit.setCreditLimit.title')}</Typography>
          <IconButton onClick={handleClose} size="small" disabled={isSubmitting}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          <Stack spacing={2.5}>
            {apiError && <Alert severity="error">{apiError}</Alert>}

            <Box>
              <Typography variant="body2" color="text.secondary">
                {t('credit.setCreditLimit.currentLimit')}
              </Typography>
              <Typography variant="h6" fontWeight={700}>
                {formatCurrency(creditLimit)}
              </Typography>
            </Box>

            <TextField
              value={limit}
              onChange={event => setLimit(event.target.value)}
              type="number"
              label={t('credit.setCreditLimit.newLimit')}
              fullWidth
              required
              autoFocus
              // The field opens prefilled with the current limit, so the label must start shrunk —
              // otherwise it sits on top of the value instead of above it.
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: 0, step: '0.01' }}
              error={submitted && limitInvalid}
              helperText={
                submitted && limitInvalid ? t('credit.setCreditLimit.newLimitInvalid') : ''
              }
              disabled={isSubmitting}
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} disabled={isSubmitting}>
            {t('credit.setCreditLimit.cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting || !accountId}
            startIcon={isSubmitting ? <CircularProgress size={16} /> : null}
          >
            {t('credit.setCreditLimit.submit')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default SetCreditLimitModal;
